import type { DashboardScore, HealthBand } from '@mvcp/contracts';

/**
 * THE band vocabulary for the whole SPA (Story 8.1) — tone, label, percentage
 * rendering, the status pill, the score number and the Framework card.
 *
 * **Why this module is web-local rather than `@mvcp/contracts`.** The web consumes
 * `@mvcp/contracts` as TYPES ONLY: importing a runtime value from it breaks
 * `vite build` (rollup cannot resolve the CJS `__exportStar`; Story 1.6 debug log).
 * That constraint is why the vocabulary cannot live in the shared contracts package —
 * it never justified five copies of it, which is exactly what the cockpit grew.
 * `import type` is the only import this file may take from `@mvcp/contracts`.
 *
 * **Exactly one declaration.** `bands.guard.test.ts` reads every source under
 * `apps/web/src` and fails if any file other than this one declares `BAND_TONE`,
 * `BAND_LABEL`, `BandPill` or `function pct` — the old docblocks' "MUST stay
 * identical" claim, enforced instead of merely asserted.
 */

/**
 * Band → pill tone. `HealthBand` is a subset of the pill tones, so the mapping is
 * the identity; keeping it explicit makes the "status is never color alone"
 * contract legible at the call site.
 */
export const BAND_TONE: Record<HealthBand, 'ok' | 'warn' | 'bad' | 'neutral'> = {
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
  neutral: 'neutral',
};

/**
 * Human status label — the SOURCE OF TRUTH beside every colored dot (never color
 * alone). Canonical per PRD §5.3 / DESIGN.md: `ok→Compliant`, `warn→Action Needed`,
 * `bad→At Risk`, `neutral→Not assessed`.
 *
 * `Not assessed` is the ONLY spelling. The Sites list, the Vendor directory and the
 * Device directory each used to hardcode a wordier variant of their own, so the same
 * subject read differently one click apart. `bands.guard.test.ts` fails if any
 * non-test source reintroduces that superseded spelling.
 */
export const BAND_LABEL: Record<HealthBand, string> = {
  ok: 'Compliant',
  warn: 'Action Needed',
  bad: 'At Risk',
  neutral: 'Not assessed',
};

/**
 * The two UNKNOWN states, which are not bands at all.
 *
 * A band says "we assessed this subject and this is the verdict". An in-flight read,
 * a failed read, and a subject missing from the payload say only that we do not know
 * — and rendering `BAND_LABEL.neutral` ("Not assessed") there would state, in
 * reassuring grey, that a possibly-`bad` vessel has nothing to score. That is the
 * false-reassurance failure Story 8.1 exists to close.
 *
 * They live HERE, beside the vocabulary, for the same reason the bands do: the Sites
 * list and the Site detail header both render them for the same vessel one click
 * apart, so two hardcoded copies would let the list and the detail word the same
 * unknown differently — the drift this module was created to make impossible. The
 * guard covers these words too.
 */
export const UNKNOWN_LABEL = {
  /** The compliance read is in flight. */
  loading: 'Checking compliance…',
  /** The read failed, or the subject has no row in the payload. */
  error: 'Compliance unavailable',
} as const;

/**
 * Render a percentage as a rounded mono value, or an em-dash when unscored.
 *
 * The engine bands this SAME rounded number (`bandForCounts`, Story 8.1), so a pill
 * beside this glyph can never contradict it.
 */
export function pct(value: number | null): string {
  return value === null ? '—' : `${Math.round(value)}%`;
}

/**
 * A pill with a leading dot AND a text label (status is never color alone).
 *
 * `label` overrides the band's canonical word for a surface that carries a richer
 * per-row status (`FrameworkDetailView`'s `statusLabel`) or a state that is not a
 * band at all ("Checking compliance…", "Compliance unavailable").
 *
 * `name` says WHICH measurement the pill carries. A surface may show two band pills
 * inches apart — a Site's worst-of HEALTH beside its compliant ÷ applicable SCORE —
 * which may legitimately disagree (`At Risk` next to `Action Needed`). Sighted users
 * get a caption to tell them apart; without a name assistive tech gets two bare band
 * words and the disagreement reads as a bug. When `name` is given the pill becomes a
 * named group, so the measurement is announced with its value.
 */
export function BandPill({
  band,
  label,
  name,
}: {
  band: HealthBand;
  label?: string;
  name?: string;
}): React.JSX.Element {
  const text = label ?? BAND_LABEL[band];
  return (
    <span
      className="status-pill"
      data-tone={BAND_TONE[band]}
      {...(name ? { role: 'group', 'aria-label': `${name}: ${text}` } : {})}
    >
      <span className="status-pill__dot" aria-hidden="true" />
      {text}
    </span>
  );
}

/**
 * How much of a score's scope was actually assessed — `"8 of 10 assessed"`, or
 * `"nothing in scope"` when there is nothing to assess (AD-22, Story 8.2).
 *
 * **A percentage never travels without its coverage.** `applicable` is the in-scope
 * subject count (assigned Vendors / installed Devices), `assessed` the part of it that
 * carries any evidence. A gap means the score was computed over a scope somebody has
 * never looked at — the reason a `90%` score can legitimately sit beside "Action
 * Needed" (the server caps the band below the top tier whenever coverage is partial;
 * the web never re-derives that, AD-8/AD-21).
 *
 * Toned to FLAG a gap: full coverage is muted context, a gap is a warning. The words
 * are the only signal that matters — the color is decoration. The warning color is
 * `--status-warn-fg`, which ships a value in BOTH themes (`styles/tokens.css`); a
 * hard-coded hex here would render dark brown on the dark surface, i.e. the story's one
 * new visual signal invisible in half the product.
 *
 * **`aria-hidden` by default — this note is the SIGHTED half of the signal.** Every
 * present call site sits beside an accessible name that already speaks the coverage
 * (`ScoreValue`'s on the three headers, `FrameworkCard`'s own `aria-label` on the
 * cards), so exposing the text as well makes a screen reader say "9 of 10 assessed"
 * twice within one region — the exact double-announcement that is documented below as
 * the reason `ProgressBar` must not repeat it, and the rule has to hold for the
 * component that introduced it. A surface where NOTHING adjacent names the coverage
 * must pass `announce` — and must then not also put it in a neighbouring name.
 */
export function CoverageNote({
  score,
  announce = false,
}: {
  score: DashboardScore;
  /** Expose the text to assistive tech: only for a surface with no adjacent spoken coverage. */
  announce?: boolean;
}): React.JSX.Element {
  const gap = score.applicable > score.assessed;
  return (
    <span
      className={gap ? undefined : 'muted'}
      style={{ fontSize: 13, ...(gap ? { color: 'var(--status-warn-fg)', fontWeight: 600 } : {}) }}
      data-coverage-gap={gap ? 'true' : undefined}
      // Tabular figures are for the "N of M" form only — `nothing in scope` is prose, and
      // stamping it numeric applies digit-width metrics to a string with no digits.
      data-numeric={score.applicable === 0 ? undefined : true}
      {...(announce ? {} : { 'aria-hidden': 'true' })}
    >
      {coverageText(score)}
    </span>
  );
}

/** The coverage sentence shared by {@link CoverageNote} and every accessible name. */
function coverageText(score: DashboardScore): string {
  return score.applicable === 0
    ? 'nothing in scope'
    : `${score.assessed} of ${score.applicable} assessed`;
}

/**
 * The big mono score number, with an accessible name that says WHICH score it is AND
 * how much of its scope was assessed.
 *
 * `role="img"` is load-bearing: an `aria-label` on a role-less `<span>` is not
 * exposed by assistive tech at all, so the fleet Overall / Framework / Site score
 * numbers used to announce as bare digits — while `getByLabelText` tests read green
 * against a name nobody could hear. `SegmentBar` is the in-repo precedent for
 * `role="img"` + `aria-label` on a graphic value.
 *
 * An unscored value renders "—" but announces "{name} not yet scored": screen
 * readers drop the em-dash, so "Site score —" would otherwise announce as a bare
 * "Site score". Deliberately the same wording `ProgressBar` uses.
 *
 * **It takes the whole `score`, not just a percentage (AD-22).** "No surface may show
 * a percentage without its coverage" is only enforceable at the type level if the
 * coverage is structurally available at every call site: a component receiving a bare
 * `percentage` *can* be rendered coverage-free and no guard test could see the
 * omission. The visible {@link CoverageNote} still sits beside the bar per view (their
 * layouts differ too much to fold in here) and is pinned per view by test.
 *
 * `ProgressBar` deliberately does NOT repeat the coverage: every score surface renders
 * the same value twice (this `role="img"` number and the `role="progressbar"` graphic),
 * so announcing it in both makes a screen reader say it twice within one region.
 */
export function ScoreValue({
  score,
  name,
}: {
  score: DashboardScore;
  /** e.g. `Overall score`, `Framework score`, `Site score`. */
  name: string;
}): React.JSX.Element {
  const spoken = score.percentage === null ? `${name} not yet scored` : `${name} ${pct(score.percentage)}`;
  return (
    <span
      className="kpi-number"
      data-numeric
      role="img"
      aria-label={`${spoken}, ${coverageText(score)}`}
    >
      {pct(score.percentage)}
    </span>
  );
}

/**
 * One Framework's summary card: code + band pill, name, the rounded score, and the
 * approved / not-approved split.
 *
 * With `onOpen` it is a real `<button>` (the Dashboard and the Directory both drill
 * into the Framework detail). Without it — the per-Site Compliance section, where
 * drilling into a company-wide Framework is not part of the surface — it is a named
 * `role="group"` carrying the identical summary as its accessible name, so assistive
 * tech gets a card identity rather than five loose text nodes.
 *
 * The `applicable - compliant` outstanding count is a display-side restatement of
 * server-computed numbers, never a second scoring path (AD-8).
 *
 * The accessible name spells an unscored value "not yet scored" for the same reason
 * `ScoreValue` does: assistive tech drops the em-dash, so `"ISO 27001 — —, Not
 * assessed"` announces as `"ISO 27001 Not assessed"` and an unscored Framework becomes
 * indistinguishable from one whose score simply was not read out. The visible glyph
 * stays "—".
 *
 * The card renders its {@link CoverageNote} and includes the coverage in its accessible
 * name (AD-22): all three card consumers — the Dashboard, the Compliance Directory and
 * the per-Site Compliance section — inherit "a percentage never travels without its
 * coverage" from this one owner, for free.
 */
export function FrameworkCard({
  code,
  name,
  score,
  onOpen,
}: {
  code: string;
  name: string;
  score: DashboardScore;
  onOpen?: () => void;
}): React.JSX.Element {
  const spoken = score.percentage === null ? 'not yet scored' : pct(score.percentage);
  const summary = `${name} — ${spoken}, ${BAND_LABEL[score.band]}, ${coverageText(score)}`;
  const body = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }} data-numeric>{code}</span>
        <BandPill band={score.band} />
      </div>
      <div style={{ fontWeight: 700, marginTop: 6 }}>{name}</div>
      {/* `metric` (21px), not `kpi-number` (32px): DESIGN.md reserves the headline size for
          the single dashboard Overall Score, and gives the per-framework percentage its own
          smaller role so a grid of cards cannot out-shout the number it rolls up into. */}
      <div className="kpi-number" data-numeric style={{ fontSize: 21, marginTop: 8 }}>
        {pct(score.percentage)}
      </div>
      {/* "not" is `applicable - compliant`, NOT `assessed - compliant`: approved + not
          must account for the WHOLE in-scope set, so an un-evidenced subject is
          not-approved (it demonstrably is not) rather than silently absent from both
          columns. Deriving it from `assessed` would render "9 approved · 0 not" beside
          "9 of 10 assessed" — a card contradicting its own coverage on exactly the case
          Story 8.2 exists to expose. The coverage note below is what says how many of
          the "not" were looked at; the split alone never claims that. Pinned by test. */}
      <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        <span data-numeric>{score.compliant}</span> approved ·{' '}
        <span data-numeric>{score.applicable - score.compliant}</span> not
      </div>
      <div style={{ marginTop: 2 }}>
        {/* Not announced: the card's own `aria-label` (built from `coverageText`) is the
            single spoken source, so exposing this too would say it twice. */}
        <CoverageNote score={score} />
      </div>
    </>
  );

  if (!onOpen) {
    return (
      <div className="framework-card" data-static="true" role="group" aria-label={summary}>
        {body}
      </div>
    );
  }
  return (
    <button type="button" className="framework-card" onClick={onOpen} aria-label={summary}>
      {body}
    </button>
  );
}
